/**
 * SIP 明细编辑表（v0.50）。
 *
 * ★★ 一个必须记住的坑：**数字字段在界面里按「字符串」存**。
 *
 *   如果按 `number` 存、受控 `<input type="number" value={n ?? ''}>`，用户想打
 *   `-0.2`：第一下敲 `-` 时 Chrome 认为这是个**非法中间态**，`e.target.value`
 *   直接给 `""` ⇒ `Number("")` 是 0/NaN ⇒ 我们把值写成 `null` ⇒ 输入框被清空，
 *   用户**永远打不出负公差**（而 `-0.2` 恰恰是最常见的那一半）。
 *   本项目反复踩过「受控控件写值时机」这类坑，所以这里：
 *     · 用 `type="text"` + `inputMode="decimal"`（手机上也弹数字键盘）；
 *     · 界面态一律 `string`，**只在提交时**转数字；
 *     · 转不出来的**原样发出去**，由后端 `_coerce_items` 报 400 并指到第几行
 *       —— 绝不在界面侧猜一个值（那就是"兜底推断"坑）。
 *
 * ★ `is_dim` 不在这里维护：它由后端按 `nominal != null` 派生（服务层纪律 2）。
 *   界面只**显示**它（决定这一行是"尺寸行"还是"文本行"的底色）。
 */
import { useState } from 'react';
import type { SipItem } from '../../types';
import { colDef, SIP_COLS_OPTIONAL } from './sipColumns';
import type { SipColsApi } from './sipColumns';
import { kindOptions, typeOptions, isUnrecognized, effectiveWarn } from './sipFormat';
import { OptionSelect } from '../OptionSelect';
import { useOptionValues } from '../../store/options';

/** 界面行：数字字段全部按**字符串**持有（见文件头）。 */
export interface SipRow {
  feature_id: string;
  kind: string;
  type: string;
  cc_sc: string;
  proj: string;
  method: string;
  /** v0.53：检验频率（自由文本）。 */
  freq: string;
  spec: string;
  warn: string;
  nominal: string;
  up: string;
  low: string;
  no: string;
  view: string;
  is_dim: boolean;
}

/** 后端行 → 界面行。`null` → 空串（**不是** `"0"`：0 与空是两回事）。 */
export function toRow(it: SipItem): SipRow {
  const s = (v: number | null | undefined) => (v == null ? '' : String(v));
  return {
    feature_id: it.feature_id ?? '',
    kind: it.kind ?? '',
    type: it.type ?? '',
    cc_sc: it.cc_sc ?? '',
    proj: it.proj ?? '',
    method: it.method ?? '',
    freq: it.freq ?? '',
    spec: it.spec ?? '',
    warn: it.warn ?? '',
    nominal: s(it.nominal),
    up: s(it.up),
    low: s(it.low),
    no: s(it.no),
    view: s(it.view),
    is_dim: !!it.is_dim,
  };
}

/** 数字串 → 数字 / null / **原样**（转不出来就原样交给后端报错，不猜）。 */
function _numLike(t: string): number | string | null {
  const v = t.trim();
  if (v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : v;
}

/** 界面行 → 请求体行。只保留后端白名单里的键（`is_dim` 不传）。 */
export function toWire(r: SipRow): Record<string, unknown> {
  return {
    feature_id: r.feature_id,
    kind: r.kind,
    type: r.type,
    cc_sc: r.cc_sc,
    proj: r.proj,
    method: r.method,
    freq: r.freq,
    spec: r.spec,
    // v0.65：`warn` **原样**发出去 —— 后端会按当前 `is_dim` 重算
    //   （`sip_draft_service._sync_no_std_warn`）：标准值填了的行，那句
    //   「无标准值」由后端摘掉再落库，不依赖界面是否先剥过一遍。
    //   ⚠️ 别改成 `effectiveWarn(r)`：那是**中文显示文案**，写进库就变成
    //      "把显示字当数据存"，下次读出来再套一层。
    warn: r.warn,
    nominal: _numLike(r.nominal),
    up: _numLike(r.up),
    low: _numLike(r.low),
    no: _numLike(r.no),
    view: _numLike(r.view),
  };
}

/** 新空行。`no` **留空**（不在这里编序号 —— 序号=图纸气泡号，见下）。 */
export function newRow(): SipRow {
  return toRow({
    feature_id: '', kind: '', type: '', cc_sc: '', proj: '', method: '',
    freq: '', spec: '', warn: '', nominal: null, up: null, low: null,
    no: null, view: 0, is_dim: false,
  });
}

/** 从「图纸特征」复制一行（加进来之后就是独立的一行，改它不影响图纸）。 */
export function rowFromItem(it: SipItem): SipRow {
  return toRow(it);
}

/**
 * v0.75 必填规则：**检验项目填了 ⇒ 量具/方法、检验频率不能空**。
 *
 * 用户 2026-10-19 原话：「必填项的根据前面检验项目如果填写了那检验工具、
 * 检验频率为必填」。后端 `sip_draft_service.required_gaps` 是同一套判据
 * （导出时按它拦），这里只负责**当场看见** —— 不用等到点导出才知道。
 *
 * ⚠️ 别把「空」写成 `!r.freq`：`freq` 是字符串，`" "` 也是真值。
 */
export function reqMissingOf(r: SipRow): string[] {
  if (!String(r.proj || '').trim()) return [];
  const miss: string[] = [];
  if (!String(r.method || '').trim()) miss.push('量具/方法');
  if (!String(r.freq || '').trim()) miss.push('检验频率');
  return miss;
}

export function SipItemsTable({
  rows, readOnly, onPatch, onMove, onRemove, onAdd, onAddFromAvailable,
  available, cols,
}: {
  rows: SipRow[];
  readOnly: boolean;
  onPatch: (i: number, patch: Partial<SipRow>) => void;
  onMove: (i: number, d: -1 | 1) => void;
  onRemove: (i: number) => void;
  onAdd: () => void;
  onAddFromAvailable: (it: SipItem) => void;
  available: SipItem[];
  /** 列显示状态（由 `useSipCols()` 提供，含持久化）。 */
  cols: SipColsApi;
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [colMenuOpen, setColMenuOpen] = useState(false);
  // v0.61：指导书的「量具/方法」与「检验频率」改由**后台清单**驱动。
  //   ★ 这两列历史上是自由文本，库里已经有「目视＋手感」这类表外写法 ——
  //     `OptionSelect` 会把它们原样补进下拉并标注「表外值」，绝不静默改掉。
  const methodOpts = useOptionValues('method');
  const freqOpts = useOptionValues('freq');
  // v0.68：「类别」可选值来自后台设置（设置页「作业指导书类别」），不再写死
  const kindOpts = useOptionValues('sip_kind');
  // 已加进表里的特征 id（只用于打「已在表中」的标 —— **不禁用**加号：
  // 同一个特征有时确实要在两份位置各列一行）
  const inTable = new Set(rows.map((r) => r.feature_id).filter(Boolean));

  /** v0.65：行级警告**按当前行现算**（标准值一填就消失，一清就回来）。
   *  一次算好再渲染 —— 判断「有没有警告」与「印什么」用同一份结果，
   *  分两处各算一遍必然出现"列表整块不显示、里面却有字"这类错位。 */
  const warnRows = rows
    .map((r, i) => ({ i, text: effectiveWarn(r) }))
    .filter((x) => x.text);

  /** v0.75：必填缺口的**行号清单**（与后端 `required_gaps` 同一判据）。 */
  const reqMiss = rows
    .map((r, i) => ({ i, miss: reqMissingOf(r) }))
    .filter((x) => x.miss.length > 0);

  /** 一格。`k` = 列 key（`sipColumns.ts` 的唯一出处）。 */
  const renderCell = (r: SipRow, i: number, k: string) => {
    // ---- 上下公差：**视觉上**并成一格，逻辑上仍是两个独立输入框。
    // 🔴 绝不能做成「一个输入框装 `+0.2/-0.1` 再拆回来」：拆错就是静默改数据，
    //    而公差是这份指导书的**判定依据**。这里保持两个 `<input>`。
    if (k === 'tol') {
      return (
        <td key={k} className="sip-td sip-td-tol">
          <span className="sip-tol">
            {/* 🔴 placeholder **留空**：原来写「上」「下」，空值时显示成
                「上 / 下」，看着像**内容**（实测第 10 行就中招）。
                空就是空框，中间那个 `/` 常显就够表意了。 */}
            <input className="sip-in" type="text" inputMode="decimal"
                   value={r.up} readOnly={readOnly} data-field="up"
                   onChange={(e) => onPatch(i, { up: e.target.value })} />
            <span className="sip-tol-sep">/</span>
            <input className="sip-in" type="text" inputMode="decimal"
                   value={r.low} readOnly={readOnly} data-field="low"
                   onChange={(e) => onPatch(i, { low: e.target.value })} />
          </span>
        </td>
      );
    }
    // ---- 类别 / 类型：改用**中文下拉**。保能力（仍可改），但不再让人手打英文枚举。
    if (k === 'kind') {
      return (
        <td key={k} className="sip-td">
          <select className="sip-sel" value={r.kind} disabled={readOnly}
                  data-field="kind"
                  onChange={(e) => onPatch(i, { kind: e.target.value })}>
            <option value="">—</option>
            {kindOptions(kindOpts, r.kind).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </td>
      );
    }
    if (k === 'type') {
      return (
        <td key={k} className="sip-td">
          <select className="sip-sel" value={r.type} disabled={readOnly}
                  data-field="type"
                  title="识别没认出来的会显示「未识别」，别把它当成正常值导出"
                  onChange={(e) => onPatch(i, { type: e.target.value })}>
            <option value="">—</option>
            {typeOptions(r.kind, r.type).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </td>
      );
    }
    if (k === 'cc_sc') {
      return (
        <td key={k} className="sip-td">
          <input className="sip-in cc" type="text" value={r.cc_sc}
                 readOnly={readOnly} data-field="cc_sc"
                 placeholder="—"
                 title="CC = 关键特性 / SC = 重要特性 / 留空 = 普通"
                 onChange={(e) => onPatch(i, { cc_sc: e.target.value })} />
        </td>
      );
    }
    if (k === '_ops') {
      return (
        <td key={k} className="sip-td sip-td-ops">
          {!readOnly && (
            <>
              <button className="sip-op" title="上移一行"
                      disabled={i === 0}
                      data-op="up"
                      onClick={() => onMove(i, -1)}>▲</button>
              <button className="sip-op" title="下移一行"
                      disabled={i === rows.length - 1}
                      data-op="down"
                      onClick={() => onMove(i, 1)}>▼</button>
              <button className="sip-op del" title="从这一份里删掉这一行（不影响图纸）"
                      data-op="del"
                      onClick={() => onRemove(i)}>✕</button>
            </>
          )}
        </td>
      );
    }
    // ---- 其余都是普通文本/数字格
    // v0.61：「量具/方法」与「检验频率」改严格下拉（用户 2026-10-17 选的口径）。
    //   ⚠️ 只读角色仍然渲染成下拉但 disabled —— 换成纯文本会让"这一格本来能选"
    //      这件事在只读账号看来消失，且与其它可选项的观感不一致。
    if (k === 'method' || k === 'freq') {
      const v = String((r as unknown as Record<string, string>)[k] ?? '');
      // v0.75：检验项目已填、这一格却空 ⇒ 当场标红（导出时后端会拦）
      const miss = !v.trim() && String(r.proj || '').trim() !== '';
      return (
        <td key={k} className={`sip-td${miss ? ' sip-rq-miss' : ''}`}
            data-req-miss={miss ? '1' : undefined}
            title={miss ? '必填：检验项目填了，这一格不能空（导出会被拦下）'
                        : colDef(k)?.hint}>
          <OptionSelect className="sip-in sip-sel-c"
                        opts={k === 'method' ? methodOpts : freqOpts}
                        value={v}
                        disabled={readOnly}
                        title={colDef(k)?.hint}
                        onChange={(nv) => onPatch(i, { [k]: nv } as Partial<SipRow>)} />
        </td>
      );
    }
    const numeric = k === 'nominal' || k === 'no' || k === 'view';
    const val = String((r as unknown as Record<string, string>)[k] ?? '');
    return (
      <td key={k} className={`sip-td${k === 'nominal' ? ' sip-td-num' : ''}`}>
        {k === 'proj' && isUnrecognized(r) && (
          <span className="sip-badge-warn"
                title="这一行的公差没识别出来，标准值留空、待人工确认">未识别</span>
        )}
        {/* ⚠️ 数字格**不再**加 `num` 类（曾导致右对齐、与左对齐的表头错位）。
            `inputMode` 保留：手机上要弹数字键盘。
            v0.72：全表改居中（见 `styles.css` 的 `.sip-in`）；
            「检验项目 / 检验标准」是**长句**，居中后超出宽度会两头被裁 ⇒
            给 `title`，悬停能看到完整原文（列宽仍是 `sipColumns.ts` 定的）。 */}
        <input className="sip-in" type="text"
               inputMode={numeric ? 'decimal' : undefined}
               value={val} readOnly={readOnly}
               data-field={k}
               title={(k === 'proj' || k === 'spec') && val ? val : undefined}
               placeholder={k === 'no' && !readOnly && val === '' ? '(自动)' : ''}
               onChange={(e) => onPatch(i, { [k]: e.target.value } as Partial<SipRow>)} />
      </td>
    );
  };

  return (
    <div className="sip-items">
      <div className="sip-items-bar">
        <span className="sip-items-title">检验明细</span>
        <span className="sip-items-note">
          序号 = 图纸气泡号（与图纸、测量报告同号）；本页不做自动重编号
        </span>
        <span className="sip-flex" />
        {/* 「列显示」是**查看偏好**（不是编辑能力）⇒ 只读账号也要能调，
            所以这个按钮不受 `readOnly` 限制。 */}
        <div className="sip-colmenu">
          <button className={`btn sm${cols.dirty ? ' primary-ghost' : ''}`}
                  onClick={() => setColMenuOpen((v) => !v)}
                  title="挑哪些列显示；选择会记住（存在本机浏览器里）">
            列显示{cols.dirty ? ' •' : ''}
          </button>
          {colMenuOpen && (
            <div className="sip-colmenu-pop" role="menu">
              {SIP_COLS_OPTIONAL.map((c) => (
                <label key={c.key} className="sip-colmenu-item" title={c.hint}>
                  <input type="checkbox" checked={cols.visible.includes(c.key)}
                         onChange={() => cols.toggle(c.key)} />
                  <span>{c.label}</span>
                </label>
              ))}
              <div className="sip-colmenu-sep" />
              <button className="btn sm" disabled={!cols.dirty}
                      onClick={() => cols.reset()}>恢复默认</button>
            </div>
          )}
        </div>
        {!readOnly && (
          <>
            <button className="btn sm" onClick={() => setDrawerOpen((v) => !v)}
                    title="从这张图纸识别出的特征里挑几行加进来">
              {drawerOpen ? '收起特征' : `从特征添加（${available.length}）`}
            </button>
            <button className="btn sm" onClick={onAdd}
                    title="加一条上面没有的检验项目（例如外观、包装等固定文案）">
              + 空行
            </button>
          </>
        )}
      </div>

      {drawerOpen && !readOnly && (
        <div className="sip-picker">
          {available.length === 0 && (
            <div className="sip-picker-empty">
              这张图纸还没有识别出任何特征 —— 先回图纸工作台跑一次识别。
            </div>
          )}
          {available.map((it, i) => (
            <button key={`${it.feature_id}_${i}`} className="sip-picker-row"
                    onClick={() => onAddFromAvailable(it)}
                    title="点一下就把这一行加到下面的明细表里">
              <span className="sip-picker-no">{it.no ?? '—'}</span>
              <span className="sip-picker-proj">{it.proj || it.feature_id || '(无项目名)'}</span>
              <span className="sip-picker-meta">
                {it.is_dim ? `标准值 ${it.nominal}` : '无标准值'}
              </span>
              {inTable.has(it.feature_id) && <em className="sip-picker-in">已在表中</em>}
            </button>
          ))}
        </div>
      )}

      {/* v0.75：必填缺口当场说清楚 —— 用户在「点导出」之前就该知道会被拦。
          行号 = **表格第几行**（与下面警告清单同一口径），不是图纸气泡号。 */}
      {reqMiss.length > 0 && (
        <div className="sip-rq-list">
          <b>必填项未填 {reqMiss.length} 行</b>
          <span>
            （第 {reqMiss.slice(0, 10).map((x) => x.i + 1).join('、')}
            {reqMiss.length > 10 ? ' …' : ''} 行）—— 检验项目填了，量具/方法和检验频率
            就不能空，否则导出会被拦下。
          </span>
        </div>
      )}

      <div className="sip-tablebox">
        <table className="sip-table">
          {/* ⚠️ 列宽的**唯一出处**是 `sipColumns.ts` 的 `w`，这里只把它搬进 DOM。
              原来宽度散在 styles.css 的 `.sip-c-*` 与 `TEXT_COLS.minWidth` **两处**，
              而输入框 `width:100%` 会让浏览器把剩余空间**均分**给所有 min-width 列
              ⇒「上公差」（内容只有 0.5）和「检验标准」一样宽。 */}
          <colgroup>
            {cols.visible.map((k) => (
              <col key={k} style={{ width: colDef(k)?.w ?? 0 }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {cols.visible.map((k) => {
                const d = colDef(k);
                if (!d) return null;
                return (
                  <th key={k} className={`sip-th sip-th-${d.key}`} title={d.hint}>
                    {d.label}
                    {/* v0.75：条件必填的列在表头打星 —— 规则来自 `requiredWhen`，
                        别在这里判「是不是量具/频率」（那就成了第二份规则）。 */}
                    {d.requiredWhen && (
                      <i className="sip-rq-star" aria-label="必填"
                         title="「检验项目」填了，这一格必填">*</i>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                {/* ⚠️ colSpan 必须等于**当前可见列数** —— 列可关之后它就不再是固定 12 */}
                <td colSpan={cols.visible.length} className="sip-empty">
                  这一份还没有任何检验项目。
                  {readOnly ? '' : ' 用上面的「从特征添加」或「+ 空行」加几条。'}
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr key={i}
                  className={`sip-row${r.is_dim ? ' dim' : ' txt'}`}
                  data-row-idx={i}>
                {cols.visible.map((k) => renderCell(r, i, k))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {warnRows.length > 0 && (
        <div className="sip-warnlist">
          {warnRows.map(({ i, text }) => (
            /* 🔴 文案由 `effectiveWarn` **按当前行现算**（v0.65）：标准值填上、
               这条提示当场消失；清空又当场回来。之前直接印 `r.warn`
               ——那是打开这一份时的快照，改完标准值它还挂着（用户报的正是这个）。
               括号里的英文枚举由 `sipFormat.ts` 换中文（唯一出处）；
               句子其余部分原样保留 ——「G 留空」指的是导出 xlsx 里那一格。 */
            <div key={i} className="sip-warnrow"
                 title="G = 导出 xlsx 里放标准值的那一格；这里留空是刻意的，不是漏填">
              <b>第 {i + 1} 行</b>{text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
